import { useState, useEffect } from "react";
import { Clock } from "lucide-react";
import Form from "react-bootstrap/Form";
import { getLabTimings, updateLabTimings } from "../../apiCalls/user";
import "../../styling/labtimings.css";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const TODAY = new Date().toLocaleDateString("en-US", { weekday: "long" });

// Fallback default if nothing is saved yet
const buildDefaults = () =>
  DAYS.map((day) => ({
    day,
    isOpen: day !== "Sunday",
    slots: [{ open: "08:00", close: "20:00" }],
  }));

// Merge fetched data with all 7 days (in case backend only has partial data)
const mergeTimings = (fetched) => {
  if (!Array.isArray(fetched) || fetched.length === 0) return buildDefaults();
  return DAYS.map((day) => {
    const saved = fetched.find((t) => t.day === day);
    return saved || { day, isOpen: false, slots: [{ open: "08:00", close: "20:00" }] };
  });
};

const LabTimings = ({ labId }) => {
  const [timings,   setTimings]   = useState(buildDefaults());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving,  setIsSaving]  = useState(false);

  // Fetch saved timings from backend
  useEffect(() => {
    const fetchTimings = async () => {
      setIsLoading(true);
      const data = await getLabTimings(labId);
      setTimings(mergeTimings(data));
      setIsLoading(false);
    };
    if (labId) fetchTimings();
  }, [labId]);

  const toggleDay = (index) => {
    setTimings((prev) =>
      prev.map((t, i) => (i === index ? { ...t, isOpen: !t.isOpen } : t))
    );
  };

  const handleSlotChange = (dayIndex, slotIndex, field, value) => {
    setTimings((prev) =>
      prev.map((t, i) => {
        if (i !== dayIndex) return t;
        const slots = t.slots.map((s, si) =>
          si === slotIndex ? { ...s, [field]: value } : s
        );
        return { ...t, slots };
      })
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    await updateLabTimings(labId, timings);
    setIsSaving(false);
  };

  return (
    <div className="form-card">
      <div className="form-card-header">
        <h5 className="form-card-title">
          <Clock size={15} /> Working Hours
        </h5>
        {/* Live open/closed indicator for today */}
        {!isLoading && (() => {
          const todayData = timings.find((t) => t.day === TODAY);
          return todayData ? (
            <span className={`lt-today-badge ${todayData.isOpen ? "lt-today-badge--open" : "lt-today-badge--closed"}`}>
              <i className="bi bi-circle-fill lt-today-dot" />
              {todayData.isOpen
                ? `Open today · ${todayData.slots[0]?.open} – ${todayData.slots[0]?.close}`
                : "Closed today"}
            </span>
          ) : null;
        })()}
      </div>

      <div className="form-card-body">
        {isLoading ? (
          <div className="lt-loading">
            <span className="svc-spinner lt-spinner" />
            Loading timings…
          </div>
        ) : (
          <div className="lt-grid">
            {timings.map((day, dayIndex) => (
              <div
                key={day.day}
                className={`lt-row ${!day.isOpen ? "lt-row--closed" : ""} ${day.day === TODAY ? "lt-row--today" : ""}`}
              >
                {/* Day name */}
                <div className="lt-day-name">
                  {day.day}
                  {day.day === TODAY && (
                    <span className="lt-day-today">Today</span>
                  )}
                </div>

                {/* Open/closed toggle */}
                <div className="lt-toggle-wrap">
                  <Form.Check
                    type="switch"
                    id={`toggle-${dayIndex}`}
                    checked={day.isOpen}
                    onChange={() => toggleDay(dayIndex)}
                  />
                  <span className={`lt-toggle-label ${day.isOpen ? "lt-toggle-label--open" : "lt-toggle-label--closed"}`}>
                    {day.isOpen ? "Open" : "Closed"}
                  </span>
                </div>

                {/* Time inputs */}
                <div className="lt-slots">
                  {day.isOpen ? (
                    day.slots.map((slot, slotIndex) => (
                      <div key={slotIndex} className="lt-slot-pair">
                        <input
                          type="time"
                          className="lt-time-input"
                          value={slot.open}
                          onChange={(e) =>
                            handleSlotChange(dayIndex, slotIndex, "open", e.target.value)
                          }
                        />
                        <span className="lt-time-sep">to</span>
                        <input
                          type="time"
                          className="lt-time-input"
                          value={slot.close}
                          onChange={(e) =>
                            handleSlotChange(dayIndex, slotIndex, "close", e.target.value)
                          }
                        />
                      </div>
                    ))
                  ) : (
                    <span className="lt-closed-text">Closed all day</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="lt-footer">
          <span className="lt-footer-hint">
            Changes are only saved when you click Save Timings
          </span>
          <button
            type="button"
            className="app-btn app-btn--primary"
            onClick={handleSave}
            disabled={isSaving || isLoading}
          >
            {isSaving
              ? <><span className="svc-spinner" /> Saving…</>
              : <><Clock size={14} /> Save Timings</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default LabTimings;
